import {
    inject,
    Injectable
} from "@angular/core";

import {
    restManager
} from "app/ajs-upgraded-providers";

interface SatispayProfilePicture {
    id: string;
    url: string;
    width: number;
    height: number;
    is_original: boolean;
}

interface SatispayProfilePictures {
    has_more: boolean;
    data: SatispayProfilePicture[];
}

interface SatispaySender {
    id?: string;
    type: string;
    name?: string;
    profile_pictures?: SatispayProfilePictures;
}

interface SatispayReceiver {
    id: string;
    type: string;
}

export interface SatispayPayment {
    id: string;
    code_identifier?: string;
    type: string;
    amount_unit: number;
    currency: string;
    status: string;
    expired: boolean;
    metadata?: Record<string, any>; // You can be more specific if you know the structure
    sender?: SatispaySender;
    receiver?: SatispayReceiver;
    insert_date: string;
    expire_date: string;
    flow?: string;
    redirect_url?: string;
}

interface SatispayGetPaymentsResponse {
    has_more: boolean;
    data: SatispayPayment[];
}

@Injectable({
    providedIn: 'root'
})
export class SatispayApiService {
    private readonly restManager = inject(restManager);

    /**
     * Sets up Satispay by linking a payment method with an authorization code.
     * @param paymentMethodId The ID of the Tilby payment method to link.
     * @param code The authorization code obtained from Satispay.
     * @returns A Promise that resolves with the API response.
     */
    setupShop(paymentMethodId: number, code: string): Promise<any> {
        return this.restManager.post('satispay/setup', { code: code }, { tilbyPaymentId: paymentMethodId });
    }

    /**
     * Creates a new Satispay payment with a MATCH_CODE flow.
     * @param paymentMethodId The ID of the Tilby payment method.
     * @param amountUnit The amount of the payment in the smallest currency unit (e.g., cents for EUR).
     * @returns A Promise that resolves with the created Satispay payment.
     */
    createMatchCodePayment(paymentMethodId: number, amountUnit: number): Promise<SatispayPayment> {
        return this.restManager.post('satispay/payments', {
            amount_unit: amountUnit,
            currency: "EUR",
            flow: "MATCH_CODE",
        }, { tilbyPaymentId: paymentMethodId });
    }

    /**
     * Retrieves a specific Satispay payment by its ID.
     * @param paymentMethodId The ID of the Tilby payment method.
     * @param paymentId The ID of the Satispay payment to retrieve.
     * @returns A Promise that resolves with the Satispay payment object.
     */
    getPaymentById(paymentMethodId: number, paymentId: string): Promise<SatispayPayment> {
        return this.restManager.getOne('satispay/payments', paymentId, { tilbyPaymentId: paymentMethodId });
    }

    /**
     * Retrieves a list of accepted Satispay payments for a given payment method.
     * @param paymentMethodId The ID of the Tilby payment method.
     * @returns A Promise that resolves with a response containing a list of Satispay payments and a 'has_more' flag.
     */
    getAcceptedPayments(paymentMethodId: number): Promise<SatispayGetPaymentsResponse> {
        return this.restManager.getList('satispay/payments', {
            tilbyPaymentId: paymentMethodId
        });
    }

    /**
     * Retrieves a list of pending Satispay payments for a given payment method.
     * @param paymentMethodId The ID of the Tilby payment method.
     * @returns A Promise that resolves with a response containing a list of Satispay payments and a 'has_more' flag.
     */
    getPendingPayments(paymentMethodId: number): Promise<SatispayGetPaymentsResponse> {
        return this.restManager.getList('satispay/payments', {
            status: 'PENDING',
            tilbyPaymentId: paymentMethodId
        });
    }

    /**
     * Accepts a specific Satispay payment.
     * @param paymentMethodId The ID of the Tilby payment method associated with the payment.
     * @param paymentId The ID of the Satispay payment to accept.
     * @returns A Promise that resolves with the updated Satispay payment.
     */
    acceptPayment(paymentMethodId: number, paymentId: string): Promise<SatispayPayment> {
        return this.restManager.put('satispay/payments', paymentId, { action: 'ACCEPT' }, { tilbyPaymentId: paymentMethodId });
    }

    /**
     * Cancels a specific Satispay payment.
     * @param paymentMethodId The ID of the Tilby payment method associated with the payment.
     * @param paymentId The ID of the Satispay payment to cancel.
     * @returns A Promise that resolves with the updated Satispay payment.
     */
    cancelPayment(paymentMethodId: number, paymentId: String): Promise<SatispayPayment> {
        return this.restManager.put('satispay/payments', paymentId, { action: 'CANCEL' }, { tilbyPaymentId: paymentMethodId });
    }

    /**
     * Initiates a rollback (refund) for a previously accepted Satispay payment.
     * @param paymentMethodId The ID of the Tilby payment method.
     * @param payment The Satispay payment object to be refunded.
     * @returns A Promise that resolves with the new Satispay payment representing the refund.
     */
    rollbackPayment(paymentMethodId: number, payment: SatispayPayment): Promise<SatispayPayment> {
        return this.restManager.post('satispay/payments', {
            flow: 'REFUND',
            amount_unit: payment.amount_unit,
            currency: payment.currency,
            parent_payment_uid: payment.id
        }, {
            tilbyPaymentId: paymentMethodId
        });
    }
}